import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import { API_ENDPOINTS } from '../../config/api';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface MerryGoRound {
  id: string;
  name: string;
  description?: string;
  amountPerRound: number;
  currentRound: number;
  totalRounds: number;
  status: 'active' | 'completed' | 'paused';
  currentRecipient?: {
    id: string;
    name: string;
    position: number;
  };
  participants: Array<{
    id: string;
    name: string;
    position: number;
    hasContributed: boolean;
    totalReceived: number;
  }>;
  nextPayoutDate?: string;
}

const ChamaMerryGoRound: React.FC = () => {
  const { chamaId } = useParams<{ chamaId: string }>();
  const colors = getThemeColors('dark');
  const [merryGoRound, setMerryGoRound] = useState<MerryGoRound | null>(null);
  const [loading, setLoading] = useState(true);
  const [contributionAmount, setContributionAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('wallet');
  const [walletBalance, setWalletBalance] = useState(0);

  useEffect(() => {
    if (chamaId) {
      loadMerryGoRound();
      loadWalletBalance();
    }
  }, [chamaId]);

  const loadMerryGoRound = async () => {
    try {
      setLoading(true);
      const authToken = localStorage.getItem('authToken');

      if (!authToken) {
        console.error('❌ ChamaMerryGoRound: No auth token found');
        return;
      }

      const response = await fetch(`${API_ENDPOINTS.CHAMA_DETAILS(chamaId)}/merry-go-round`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setMerryGoRound(data.data);
          setContributionAmount(data.data.amountPerRound?.toString() || '');
        } else {
          setMerryGoRound(null);
        }
      } else {
        console.error('❌ ChamaMerryGoRound: Failed to load merry-go-round');
        setMerryGoRound(null);
      }
    } catch (error) {
      console.error('❌ ChamaMerryGoRound: Error loading merry-go-round:', error);
      setMerryGoRound(null);
    } finally {
      setLoading(false);
    }
  };

  const loadWalletBalance = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const response = await fetch(API_ENDPOINTS.WALLET_BALANCE, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setWalletBalance(data.data.balance || 0);
        }
      }
    } catch (error) {
      console.error('Failed to load wallet balance:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleContribute = async () => {
    if (!contributionAmount || parseFloat(contributionAmount) <= 0) {
      alert('Please enter a valid contribution amount');
      return;
    }

    if (!merryGoRound) {
      alert('Merry-go-round data not loaded');
      return;
    }

    if (paymentMethod === 'wallet' && parseFloat(contributionAmount) > walletBalance) {
      alert(`Insufficient wallet balance. You need KES ${formatCurrency(parseFloat(contributionAmount) - walletBalance)} more.`);
      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        alert('Authentication required. Please log in again.');
        return;
      }

      const contributionData = {
        chamaId: chamaId,
        merryGoRoundId: merryGoRound.id,
        amount: parseFloat(contributionAmount),
        paymentMethod: paymentMethod,
        type: 'merry-go-round',
      };

      const response = await fetch(API_ENDPOINTS.CONTRIBUTIONS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contributionData),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert('Merry-go-round contribution successful!');
          loadMerryGoRound(); // Refresh data
          loadWalletBalance(); // Refresh wallet balance
        } else {
          alert(`Contribution failed: ${data.error || 'Unknown error'}`);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Contribution failed: ${errorData.error || 'Network error'}`);
      }
    } catch (error) {
      console.error('Contribution error:', error);
      alert('Network error: Unable to process contribution. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading merry-go-round...</div>
        </div>
      </div>
    );
  }

  if (!merryGoRound) {
    return (
      <div style={styles.container}>
        <UserDashboardHeader
          title="Merry-Go-Round"
          showBackButton={true}
          showHomeButton={false}
        />

        <div style={styles.scrollableContent}>
          <div style={styles.content}>
            <div style={{ ...styles.emptyState, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <div style={styles.emptyIcon}>🔄</div>
              <div style={{ ...styles.emptyTitle, color: colors.text }}>No Active Merry-Go-Round</div>
              <div style={{ ...styles.emptySubtitle, color: colors.textSecondary }}>
                There is no active merry-go-round in this chama. Create one to start rotating savings.
              </div>
              <button style={{ ...styles.createButton, backgroundColor: colors.primary }}>
                Create Merry-Go-Round
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentUserParticipation = merryGoRound.participants.find(p => p.id === 'current-user'); // Replace with actual user ID check
  const hasUserContributed = currentUserParticipation?.hasContributed || false;

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title={`${merryGoRound.name} - Merry-Go-Round`}
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Merry-Go-Round Overview */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <div style={styles.mgrHeader}>
              <div style={styles.mgrIcon}>🔄</div>
              <div style={styles.mgrInfo}>
                <h3 style={{ ...styles.mgrName, color: colors.text }}>{merryGoRound.name}</h3>
                <p style={{ ...styles.mgrDescription, color: colors.textSecondary }}>
                  {merryGoRound.description || 'Rotating savings circle'}
                </p>
              </div>
              <div style={{ ...styles.mgrStatus, backgroundColor: merryGoRound.status === 'active' ? colors.success + '20' : colors.warning + '20' }}>
                <span style={{ color: merryGoRound.status === 'active' ? colors.success : colors.warning }}>
                  {merryGoRound.status.charAt(0).toUpperCase() + merryGoRound.status.slice(1)}
                </span>
              </div>
            </div>

            <div style={styles.mgrStats}>
              <div style={styles.statItem}>
                <div style={{ ...styles.statValue, color: colors.text }}>
                  Round {merryGoRound.currentRound}/{merryGoRound.totalRounds}
                </div>
                <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Current Round</div>
              </div>
              <div style={styles.statItem}>
                <div style={{ ...styles.statValue, color: colors.text }}>
                  {formatCurrency(merryGoRound.amountPerRound)}
                </div>
                <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Per Round</div>
              </div>
              <div style={styles.statItem}>
                <div style={{ ...styles.statValue, color: colors.text }}>
                  {merryGoRound.participants.length}
                </div>
                <div style={{ ...styles.statLabel, color: colors.textSecondary }}>Participants</div>
              </div>
            </div>
          </div>

          {/* Current Recipient */}
          {merryGoRound.currentRecipient && (
            <div style={{ ...styles.card, backgroundColor: colors.primary + '10', border: `1px solid ${colors.primary}` }}>
              <div style={styles.recipientHeader}>
                <div style={styles.recipientIcon}>🎯</div>
                <div style={styles.recipientInfo}>
                  <div style={{ ...styles.recipientLabel, color: colors.textSecondary }}>Current Recipient</div>
                  <div style={{ ...styles.recipientName, color: colors.primary }}>{merryGoRound.currentRecipient.name}</div>
                  <div style={{ ...styles.recipientPosition, color: colors.textSecondary }}>
                    Position {merryGoRound.currentRecipient.position}
                  </div>
                </div>
              </div>

              {merryGoRound.nextPayoutDate && (
                <div style={styles.payoutInfo}>
                  <div style={{ ...styles.payoutLabel, color: colors.textSecondary }}>Next Payout</div>
                  <div style={{ ...styles.payoutDate, color: colors.text }}>
                    {new Date(merryGoRound.nextPayoutDate).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contribution Status */}
          <div style={{ ...styles.card, backgroundColor: hasUserContributed ? colors.success + '10' : colors.warning + '10', border: `1px solid ${hasUserContributed ? colors.success : colors.warning}` }}>
            <div style={styles.contributionStatus}>
              <div style={styles.statusIcon}>
                {hasUserContributed ? '✅' : '⏳'}
              </div>
              <div style={styles.statusInfo}>
                <div style={{ ...styles.statusTitle, color: hasUserContributed ? colors.success : colors.warning }}>
                  {hasUserContributed ? 'You Have Contributed' : 'Awaiting Your Contribution'}
                </div>
                <div style={{ ...styles.statusMessage, color: colors.textSecondary }}>
                  {hasUserContributed
                    ? `You successfully contributed ${formatCurrency(merryGoRound.amountPerRound)} to this round.`
                    : `Contribute exactly ${formatCurrency(merryGoRound.amountPerRound)} to participate in this round.`
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Contribution Form */}
          {!hasUserContributed && (
            <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
              <h3 style={{ ...styles.cardTitle, color: colors.text }}>Make Contribution</h3>

              <div style={styles.formGroup}>
                <label style={{ ...styles.label, color: colors.text }}>Contribution Amount</label>
                <div style={{ ...styles.amountDisplay, backgroundColor: colors.surface, border: `1px solid ${colors.border}`, color: colors.text }}>
                  {formatCurrency(parseFloat(contributionAmount) || 0)}
                  <span style={{ ...styles.amountNote, color: colors.textSecondary }}>
                    (Fixed amount for merry-go-round)
                  </span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={{ ...styles.label, color: colors.text }}>Payment Method</label>
                <div style={styles.paymentMethods}>
                  <div
                    style={{
                      ...styles.paymentMethod,
                      backgroundColor: paymentMethod === 'wallet' ? colors.primary + '20' : colors.surface,
                      border: `2px solid ${paymentMethod === 'wallet' ? colors.primary : colors.border}`,
                    }}
                    onClick={() => setPaymentMethod('wallet')}
                  >
                    <span style={{ fontSize: '24px', marginBottom: '8px' }}>💰</span>
                    <div style={{ ...styles.paymentMethodText, color: colors.text }}>VaultKe Wallet</div>
                    <div style={{ ...styles.balanceText, color: colors.textSecondary }}>
                      Balance: {formatCurrency(walletBalance)}
                    </div>
                  </div>

                  <div
                    style={{
                      ...styles.paymentMethod,
                      backgroundColor: paymentMethod === 'mpesa' ? colors.success + '20' : colors.surface,
                      border: `2px solid ${paymentMethod === 'mpesa' ? colors.success : colors.border}`,
                    }}
                    onClick={() => setPaymentMethod('mpesa')}
                  >
                    <span style={{ fontSize: '24px', marginBottom: '8px' }}>📱</span>
                    <div style={{ ...styles.paymentMethodText, color: colors.text }}>M-Pesa</div>
                    <div style={{ ...styles.balanceText, color: colors.textSecondary }}>
                      Mobile Money
                    </div>
                  </div>
                </div>
              </div>

              {/* Validation Messages */}
              {paymentMethod === 'wallet' && contributionAmount && parseFloat(contributionAmount) > walletBalance && (
                <div style={{ ...styles.validationMessage, backgroundColor: colors.error + '20', border: `1px solid ${colors.error}` }}>
                  <span style={{ color: colors.error }}>
                    ⚠️ Insufficient balance. You need {formatCurrency(parseFloat(contributionAmount) - walletBalance)} more.
                  </span>
                </div>
              )}

              <button
                onClick={handleContribute}
                disabled={hasUserContributed || !contributionAmount || parseFloat(contributionAmount) <= 0 || (paymentMethod === 'wallet' && parseFloat(contributionAmount) > walletBalance)}
                style={{
                  ...styles.contributeButton,
                  backgroundColor: (hasUserContributed || !contributionAmount || parseFloat(contributionAmount) <= 0 || (paymentMethod === 'wallet' && parseFloat(contributionAmount) > walletBalance))
                    ? colors.textSecondary
                    : colors.primary,
                  cursor: (hasUserContributed || !contributionAmount || parseFloat(contributionAmount) <= 0 || (paymentMethod === 'wallet' && parseFloat(contributionAmount) > walletBalance))
                    ? 'not-allowed'
                    : 'pointer',
                }}
              >
                {hasUserContributed ? '✅ Already Contributed' : 'Contribute to Merry-Go-Round'}
              </button>
            </div>
          )}

          {/* Participants List */}
          <div style={{ ...styles.card, backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
            <h3 style={{ ...styles.cardTitle, color: colors.text }}>Participants</h3>
            <div style={styles.participantsList}>
              {merryGoRound.participants.map((participant, index) => (
                <div key={participant.id} style={styles.participantItem}>
                  <div style={styles.participantInfo}>
                    <div style={styles.participantPosition}>{participant.position}</div>
                    <div style={styles.participantName}>{participant.name}</div>
                    {participant.hasContributed && (
                      <div style={styles.contributedBadge}>✅ Contributed</div>
                    )}
                  </div>
                  <div style={styles.participantAmount}>
                    {formatCurrency(participant.totalReceived)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--background-color)',
    paddingBottom: 80,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: 80,
    paddingBottom: 80,
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: spacing.md,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xxxl,
  },
  card: {
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    marginBottom: spacing.md,
    ...getShadowStyle('sm'),
  },
  cardTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    margin: 0,
    marginBottom: spacing.md,
  },
  emptyState: {
    textAlign: 'center',
    padding: spacing.xl,
    borderRadius: borderRadius.lg,
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: spacing.md,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
  },
  emptySubtitle: {
    fontSize: typography.fontSize.base,
    marginBottom: spacing.lg,
  },
  createButton: {
    padding: `${spacing.md} ${spacing.lg}`,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
  },
  mgrHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  mgrIcon: {
    fontSize: '32px',
    marginRight: spacing.md,
  },
  mgrInfo: {
    flex: 1,
  },
  mgrName: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    margin: 0,
    marginBottom: spacing.xs,
  },
  mgrDescription: {
    fontSize: typography.fontSize.sm,
    margin: 0,
  },
  mgrStatus: {
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.md,
  },
  mgrStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: spacing.md,
  },
  statItem: {
    textAlign: 'center',
  },
  statValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
  },
  statLabel: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  recipientHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  recipientIcon: {
    fontSize: '24px',
    marginRight: spacing.md,
  },
  recipientInfo: {
    flex: 1,
  },
  recipientLabel: {
    fontSize: typography.fontSize.sm,
  },
  recipientName: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    marginTop: spacing.xs / 2,
  },
  recipientPosition: {
    fontSize: typography.fontSize.sm,
    marginTop: spacing.xs / 2,
  },
  payoutInfo: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  payoutLabel: {
    fontSize: typography.fontSize.sm,
  },
  payoutDate: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginTop: spacing.xs / 2,
  },
  contributionStatus: {
    display: 'flex',
    alignItems: 'center',
  },
  statusIcon: {
    fontSize: '32px',
    marginRight: spacing.md,
  },
  statusInfo: {
    flex: 1,
  },
  statusTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    marginBottom: spacing.xs,
  },
  statusMessage: {
    fontSize: typography.fontSize.sm,
  },
  formGroup: {
    marginBottom: spacing.lg,
  },
  label: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.sm,
    display: 'block',
  },
  amountDisplay: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  amountNote: {
    fontSize: typography.fontSize.sm,
    fontWeight: 'normal',
  },
  paymentMethods: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: spacing.md,
  },
  paymentMethod: {
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'all 0.2s ease',
  },
  paymentMethodText: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing.xs,
  },
  balanceText: {
    fontSize: typography.fontSize.sm,
  },
  validationMessage: {
    padding: spacing.md,
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
  },
  contributeButton: {
    width: '100%',
    padding: spacing.md,
    borderRadius: borderRadius.lg,
    border: 'none',
    color: 'white',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginTop: spacing.md,
  },
  participantsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
  },
  participantItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: borderRadius.md,
  },
  participantInfo: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  participantPosition: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(33, 150, 243, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold,
    color: '#2196F3',
    marginRight: spacing.md,
  },
  participantName: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    color: 'inherit',
    flex: 1,
  },
  contributedBadge: {
    fontSize: typography.fontSize.sm,
    color: '#10B981',
    marginLeft: spacing.sm,
  },
  participantAmount: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.bold,
    color: 'inherit',
  },
};

export default ChamaMerryGoRound;